import { isForbidden } from '@/helpers/stores/axiosErrorCheck'
import type { PostFlags, GetFlags } from '@/types'

/**
 * Determines whether a POST action should be retried for an assigned endpoint.
 *
 * @template T - The type of the payload, which must extend `PostFlags`.
 * @param error - The error object to check, typically from a failed request.
 * @param payload - The payload object containing post flags.
 * @returns `true` if the error is forbidden, the `postAssignedFlag` is set,
 *          and `postAssignedFirst` is not set; otherwise, `false`.
 */
export const shouldRetryPostActionForAssignedEndpoint = <T>(
  error: unknown,
  payload: T & PostFlags
): boolean => {
  return isForbidden(error) && payload?.postAssignedFlag && !payload?.postAssignedFirst
}

/**
 * Determines whether a GET action for an assigned endpoint should be retried.
 *
 * This function checks if the error is a "forbidden" error and if the payload
 * indicates that the assigned flag should be retrieved, but not for the first time.
 *
 * @typeParam T - The type of the payload, which must extend `GetFlags`.
 * @param error - The error object to check.
 * @param payload - The payload containing flags that control retry logic.
 * @returns `true` if the action should be retried, otherwise `false`.
 */
export const shouldRetryGetActionForAssignedEndpoint = <T>(
  error: unknown,
  payload: T & GetFlags
): boolean => {
  return isForbidden(error) && payload.getAssignedFlag && !payload.getAssignedFirst
}
